// BUILD-046 tasks 8.24 / 8.25 (supporting model): the flow that runs ONE action control on ONE row - Assign to…, Recovery assignment…,
// Reassign…, Return to queue, Reopen (design "Oversight actions", "Authorized reassignment path"; Req 4.20 - 4.24, 4.33 - 4.39, 14.21, 14.30).
//
// The server is authoritative. This flow only sequences what the screen does:
//   idle -> choosing (a control that needs a person: the picker opens, the candidates load) -> sending -> idle + outcome
//   idle -> confirming (Return to queue, Reopen: one confirmation) -> sending -> idle + outcome
//   sending -> back to choosing / confirming + a REFUSAL when the server (or the network) says no.
//
//  - Candidates for the picker come from `GET /work/employees` (the existing contract: no new route). That list is NOT filtered to the
//    task's eligible people, so the server's rejection of anything ineligible or out of scope (Req 4.35) is the control, and it is shown as a
//    refusal with the server's own message. The current owner is left out of a Reassign picker. A candidate list that fails to load is an error
//    with Retry, never an empty picker.
//  - Every answer other than success is a refusal. It carries the server's message, leaves the choice and the screen exactly as they were
//    (nothing is applied locally), and a second activation while the request runs sends nothing.
//  - An answer that never came (timeout, no connection) says the result could not be confirmed, because the change may have gone through.
//  - The outcome never says "completed". A recovery assignment says it was recorded as an explicit assignment and did not add the person to
//    the queue (Req 4.34); a return to an empty queue says the task stays visible to oversight as Reassignment needed (Req 4.38).
//  - A Caller change drops the flow (`callerChangedFlow`): nothing the previous Caller chose is sent as the new Caller.
//
// Pure module apart from the injected api. Runtime imports: the list-state model and the action model (both pure). Erasable TypeScript only.

import { initialListState, isStaleOutcome, listReducer, toListError } from './listStateModel';
import type { ListState } from './listStateModel';
import { CONTROL_TEXT } from './oversightActionsModel';
import type { ActionControl } from './oversightActionsModel';
import { personDisplay } from './displayNames';

export interface Candidate {
  employeeId: string;
  displayName: string;
  department?: string;
}

export type FlowPhase = 'idle' | 'choosing' | 'confirming' | 'sending';

export interface FlowFailure {
  headline: string;
  detail: string | null;
}

export interface ActionFlowState {
  phase: FlowPhase;
  control: ActionControl | null;
  /** The person chosen in the picker; '' until one is chosen. */
  targetEmployeeId: string;
  candidates: ListState<Candidate>;
  failure: FlowFailure | null;
  /** What the last accepted action did, shown until it is dismissed or another control opens. */
  outcome: string | null;
}

export function initialActionFlow(): ActionFlowState {
  return { phase: 'idle', control: null, targetEmployeeId: '', candidates: initialListState<Candidate>(), failure: null, outcome: null };
}

/** A Caller change: the flow and its candidate list are dropped (the list belonged to the previous Caller's view of the company). */
export function callerChangedFlow(): ActionFlowState {
  return initialActionFlow();
}

// ───────────────────────── opening, choosing, closing ─────────────────────────

/** Opens a control: a picker for a control that needs a person, a confirmation for the others. Not while a request runs. */
export function openControl(state: ActionFlowState, control: ActionControl): ActionFlowState {
  if (state.phase === 'sending') return state;
  const needsTarget = CONTROL_TEXT[control].needsTarget;
  return { ...state, phase: needsTarget ? 'choosing' : 'confirming', control, targetEmployeeId: '', failure: null, outcome: null };
}

export function selectTarget(state: ActionFlowState, employeeId: string): ActionFlowState {
  if (state.phase !== 'choosing') return state;
  return { ...state, targetEmployeeId: employeeId, failure: null };
}

/** Closes the open control without sending anything. Not while a request runs. */
export function closeControl(state: ActionFlowState): ActionFlowState {
  if (state.phase === 'idle' || state.phase === 'sending') return state;
  return { ...state, phase: 'idle', control: null, targetEmployeeId: '', failure: null };
}

export function dismissOutcome(state: ActionFlowState): ActionFlowState {
  return state.outcome === null ? state : { ...state, outcome: null };
}

// ───────────────────────── the candidate list ─────────────────────────

export interface CandidatesApi {
  getCallerEpoch: () => number;
  listEmployees: () => Promise<{ employees: Candidate[] }>;
}

export interface FlowStore {
  get: () => ActionFlowState;
  set: (next: ActionFlowState) => void;
}

/** The candidates are needed and are not loaded (or loading) yet. */
export function needsCandidates(state: ActionFlowState): boolean {
  const c = state.candidates;
  return state.phase === 'choosing' && c.phase === 'loading' && c.seq === 0;
}

/** Any flow state that holds a candidate list (this flow's, and the issuer flow's of task 8.23). */
export interface CandidateHolder {
  candidates: ListState<Candidate>;
}

/** Loads (or reloads, for Retry) the candidate list. A failure is the list's own error; an answer for a previous Caller is dropped. */
export async function loadCandidates<S extends CandidateHolder>(api: CandidatesApi, store: { get: () => S; set: (next: S) => void }): Promise<void> {
  const epoch = api.getCallerEpoch();
  const before = store.get();
  const seq = before.candidates.epoch === epoch ? before.candidates.seq + 1 : 1;
  store.set({ ...before, candidates: listReducer(before.candidates, { type: 'request', epoch, seq }) });
  try {
    const answer = await api.listEmployees();
    const now = store.get();
    store.set({ ...now, candidates: listReducer(now.candidates, { type: 'success', epoch, seq, items: answer.employees }) });
  } catch (thrown) {
    if (isStaleOutcome(thrown)) return;
    const now = store.get();
    store.set({ ...now, candidates: listReducer(now.candidates, { type: 'failure', epoch, seq, error: toListError(thrown) }) });
  }
}

// ───────────────────────── sending ─────────────────────────

export interface ActionRequest {
  control: ActionControl;
  taskId: string;
  /** The person to give the task to; null for Return to queue and Reopen. */
  targetEmployeeId: string | null;
  /** The owner the screen showed (null = unclaimed), so the server can refuse a change made since the row was read. */
  expectedOwnerEmployeeId: string | null;
}

export interface ActionContext {
  taskId: string;
  /** The row's current owner; '' for an unclaimed row. */
  ownerEmployeeId: string;
}

/** The click on the confirm button. `send` is non-null exactly when the caller must now send the request. */
export function confirmStep(state: ActionFlowState, ctx: ActionContext): { state: ActionFlowState; send: ActionRequest | null } {
  if (state.phase === 'sending' || state.control === null) return { state, send: null };
  const needsTarget = CONTROL_TEXT[state.control].needsTarget;
  if (state.phase !== (needsTarget ? 'choosing' : 'confirming')) return { state, send: null };
  if (needsTarget && state.targetEmployeeId === '') return { state, send: null };
  const owner = ctx.ownerEmployeeId === '' ? null : ctx.ownerEmployeeId;
  const send: ActionRequest = {
    control: state.control,
    taskId: ctx.taskId,
    targetEmployeeId: needsTarget ? state.targetEmployeeId : null,
    expectedOwnerEmployeeId: owner,
  };
  return { state: { ...state, phase: 'sending', failure: null }, send };
}

export interface ActionResponse {
  item?: { ownerName?: unknown; reassignmentNeeded?: unknown } | null;
  recovery?: unknown;
}

function candidateName(state: ActionFlowState, employeeId: string, response: ActionResponse): string {
  // D1 display cleanup: the person is written First name + last initial (a role word such as Manager is never a surname) and without the "(FICTIONAL)" test marker.
  const found = state.candidates.items.find((c) => c.employeeId === employeeId);
  const shown = (name: string) => {
    const short = personDisplay(name, { department: found?.department }).short;
    return short !== '' ? short : name.trim();
  };
  const fromServer = response.item?.ownerName;
  if (typeof fromServer === 'string' && fromServer.trim() !== '') return shown(fromServer);
  return found !== undefined && found.displayName.trim() !== '' ? shown(found.displayName) : 'the person you chose';
}

/** What the accepted action did, in words. Never "completed". */
export function outcomeText(state: ActionFlowState, request: ActionRequest, response: ActionResponse): string {
  const name = request.targetEmployeeId === null ? '' : candidateName(state, request.targetEmployeeId, response);
  switch (request.control) {
    case 'assign-to':
    case 'recovery-assign':
      return response.recovery === true ? `Assigned to ${name} as a recovery assignment. ${name} was not added to the queue.` : `Assigned to ${name}.`;
    case 'reassign':
      return `Reassigned to ${name}.`;
    case 'return-to-queue':
      return response.item?.reassignmentNeeded === true
        ? 'Returned to the queue. No one in the queue can take it yet, so it stays visible to oversight as Reassignment needed. Use Recovery assignment… to give it to someone.'
        : 'Returned to the queue.';
    case 'reopen':
      return 'Reopened.';
  }
}

/** The request was accepted: the flow closes and says what happened. Applies only to a request in progress. */
export function actionSucceeded(state: ActionFlowState, request: ActionRequest, response: ActionResponse): ActionFlowState {
  if (state.phase !== 'sending') return state;
  return { ...state, phase: 'idle', control: null, targetEmployeeId: '', failure: null, outcome: outcomeText(state, request, response) };
}

export const NOT_CHANGED = 'Not changed.';
export const NOT_CONFIRMED = 'The result could not be confirmed.';

/** A refusal (or a failure to get an answer). Nothing on the screen changes: the same control stays open with the same choice. */
export function describeActionFailure(thrown: unknown): FlowFailure {
  if (thrown !== null && typeof thrown === 'object') {
    const t = thrown as { kind?: unknown; message?: unknown };
    if (t.kind === 'timeout' || t.kind === 'network') {
      return { headline: NOT_CONFIRMED, detail: 'The change may have gone through. Reload the list to see the current state before trying again.' };
    }
    if (typeof t.message === 'string' && t.message.trim() !== '') return { headline: NOT_CHANGED, detail: t.message.trim() };
  }
  return { headline: NOT_CHANGED, detail: 'The server refused the change.' };
}

export function actionFailed(state: ActionFlowState, thrown: unknown): ActionFlowState {
  if (state.phase !== 'sending' || state.control === null) return state;
  if (isStaleOutcome(thrown)) return state;
  const back: FlowPhase = CONTROL_TEXT[state.control].needsTarget ? 'choosing' : 'confirming';
  return { ...state, phase: back, failure: describeActionFailure(thrown) };
}

// ───────────────────────── the flow the component runs ─────────────────────────

export interface ActionApi extends CandidatesApi {
  assignTo: (id: string, input: { targetEmployeeId: string; expectedOwnerEmployeeId?: string | null }) => Promise<ActionResponse>;
  reassign: (id: string, input: { targetEmployeeId: string; expectedOwnerEmployeeId?: string | null }) => Promise<ActionResponse>;
  returnToQueue: (id: string, input: { expectedOwnerEmployeeId?: string | null }) => Promise<ActionResponse>;
  restore: (id: string) => Promise<ActionResponse>;
}

function send(api: ActionApi, request: ActionRequest): Promise<ActionResponse> {
  switch (request.control) {
    case 'assign-to':
    case 'recovery-assign':
      return api.assignTo(request.taskId, { targetEmployeeId: request.targetEmployeeId ?? '', expectedOwnerEmployeeId: request.expectedOwnerEmployeeId });
    case 'reassign':
      return api.reassign(request.taskId, { targetEmployeeId: request.targetEmployeeId ?? '', expectedOwnerEmployeeId: request.expectedOwnerEmployeeId });
    case 'return-to-queue':
      return api.returnToQueue(request.taskId, { expectedOwnerEmployeeId: request.expectedOwnerEmployeeId });
    case 'reopen':
      return api.restore(request.taskId);
  }
}

export type ActionOutcome = { kind: 'ignored' } | { kind: 'done'; request: ActionRequest; response: ActionResponse } | { kind: 'failed' } | { kind: 'dropped' };

/**
 * Runs the open control. The state changes before the request is sent; a repeat activation while it runs sends nothing. On `done` the
 * caller reloads the lists the change touched and fires `shared-tasks:changed`; on `failed` nothing is reloaded and the screen is unchanged.
 */
export async function performAction(api: ActionApi, store: FlowStore, ctx: ActionContext): Promise<ActionOutcome> {
  const step = confirmStep(store.get(), ctx);
  if (step.send === null) return { kind: 'ignored' };
  store.set(step.state);
  try {
    const response = await send(api, step.send);
    store.set(actionSucceeded(store.get(), step.send, response));
    return { kind: 'done', request: step.send, response };
  } catch (thrown) {
    if (isStaleOutcome(thrown)) return { kind: 'dropped' };
    store.set(actionFailed(store.get(), thrown));
    return { kind: 'failed' };
  }
}

// ───────────────────────── what the row shows ─────────────────────────

export interface FlowView {
  open: boolean;
  control: ActionControl | null;
  /** The sentence for the open control (the picker prompt or the confirmation). */
  prompt: string | null;
  needsTarget: boolean;
  /** The people the picker offers (the current owner is left out). Empty while the list is not `data`. */
  options: Candidate[];
  candidates: ListState<Candidate>;
  selected: string;
  confirm: { label: string; disabled: boolean };
  sending: boolean;
  failure: FlowFailure | null;
  outcome: string | null;
}

export function describeFlow(state: ActionFlowState, ctx: { ownerEmployeeId: string }): FlowView {
  const control = state.control;
  const text = control === null ? null : CONTROL_TEXT[control];
  const open = state.phase !== 'idle' && control !== null;
  const options = state.candidates.phase === 'data' ? state.candidates.items.filter((c) => c.employeeId !== ctx.ownerEmployeeId) : [];
  const needsTarget = text?.needsTarget === true;
  return {
    open,
    control: open ? control : null,
    prompt: open && text !== null ? text.prompt : null,
    needsTarget,
    options,
    candidates: state.candidates,
    selected: state.targetEmployeeId,
    confirm: {
      label: state.phase === 'sending' ? 'Saving…' : text === null ? '' : text.confirmLabel,
      disabled: state.phase === 'sending' || (needsTarget && state.targetEmployeeId === ''),
    },
    sending: state.phase === 'sending',
    failure: state.failure,
    outcome: state.outcome,
  };
}
